package com.karyadina.cloudsync.service

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.delete
import io.ktor.client.request.setBody
import io.ktor.client.request.header
import io.ktor.client.statement.readBytes
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.HttpHeaders
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonPrimitive

/**
 * Real Google Drive Service using Ktor REST API
 */
class GoogleDriveServiceImpl(
    private val googleAuthService: GoogleAuthService,
    private val httpClient: HttpClient = HttpClient()
) : GoogleDriveService {
    
    override suspend fun uploadFile(fileName: String, encryptedData: ByteArray): Result<String> {
        val tokenResult = googleAuthService.getToken()
        if (tokenResult.isFailure) return Result.failure(tokenResult.exceptionOrNull()!!)
        
        return try {
            val token = tokenResult.getOrThrow()
            
            // For a real multipart upload, we create the boundary
            val boundary = "CloudSyncBoundary"
            val metadata = """
                {"name": "$fileName", "parents": ["appDataFolder"]}
            """.trimIndent()
            
            val bodyData = buildString {
                append("--$boundary\r\n")
                append("Content-Type: application/json; charset=UTF-8\r\n\r\n")
                append(metadata)
                append("\r\n--$boundary\r\n")
                append("Content-Type: application/octet-stream\r\n\r\n")
            }.encodeToByteArray() + encryptedData + "\r\n--$boundary--".encodeToByteArray()

            val response = httpClient.post("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart") {
                header(HttpHeaders.Authorization, "Bearer $token")
                header(HttpHeaders.ContentType, "multipart/related; boundary=$boundary")
                setBody(bodyData)
            }
            
            val responseText = response.bodyAsText()
            val fileId = Json.parseToJsonElement(responseText).jsonObject["id"]?.jsonPrimitive?.content
            
            if (fileId != null) Result.success(fileId)
            else Result.failure(Exception("Upload failed: $responseText"))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    
    override suspend fun downloadFile(fileId: String): Result<ByteArray> {
        val tokenResult = googleAuthService.getToken()
        if (tokenResult.isFailure) return Result.failure(tokenResult.exceptionOrNull()!!)
        
        return try {
            val token = tokenResult.getOrThrow()
            val response = httpClient.get("https://www.googleapis.com/drive/v3/files/$fileId?alt=media") {
                header(HttpHeaders.Authorization, "Bearer $token")
            }
            Result.success(response.readBytes())
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    
    override suspend fun listFiles(): Result<List<DriveFileInfo>> {
        val tokenResult = googleAuthService.getToken()
        if (tokenResult.isFailure) return Result.failure(tokenResult.exceptionOrNull()!!)
        
        return try {
            val token = tokenResult.getOrThrow()
            val response = httpClient.get("https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&fields=files(id,name,size,modifiedTime)") {
                header(HttpHeaders.Authorization, "Bearer $token")
            }
            val responseText = response.bodyAsText()
            val filesArray = Json.parseToJsonElement(responseText).jsonObject["files"]?.jsonArray
            
            val filesList = filesArray?.map { element ->
                val obj = element.jsonObject
                DriveFileInfo(
                    fileId = obj["id"]?.jsonPrimitive?.content ?: "",
                    fileName = obj["name"]?.jsonPrimitive?.content ?: "",
                    size = obj["size"]?.jsonPrimitive?.content?.toLongOrNull() ?: 0L,
                    modifiedTime = parseIso8601(obj["modifiedTime"]?.jsonPrimitive?.content)
                )
            } ?: emptyList()
            
            Result.success(filesList)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private fun parseIso8601(isoString: String?): Long {
        if (isoString == null) return 0L
        return try {
            com.karyadina.cloudsync.util.TimeUtil.parseIso8601(isoString)
        } catch (e: Exception) {
            0L
        }
    }
    
    override suspend fun deleteFile(fileId: String): Result<Unit> {
        val tokenResult = googleAuthService.getToken()
        if (tokenResult.isFailure) return Result.failure(tokenResult.exceptionOrNull()!!)
        
        return try {
            val token = tokenResult.getOrThrow()
            httpClient.delete("https://www.googleapis.com/drive/v3/files/$fileId") {
                header(HttpHeaders.Authorization, "Bearer $token")
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    
    override suspend fun getFileSize(fileName: String): Result<Long> {
        return Result.success(0L)
    }
    
    override suspend fun isAuthenticated(): Result<Boolean> {
        return Result.success(googleAuthService.isAuthenticated())
    }
    
    override suspend fun getTotalStorageUsed(): Result<Long> {
        return Result.success(0L)
    }
}
